import tw, { styled } from 'twin.macro';
import React, { useCallback, useState } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { useStoreState } from 'easy-peasy';

import { MdEmail } from 'react-icons/md';
import { BiMenuAltRight } from 'react-icons/bi';
import { AiOutlineClose } from 'react-icons/ai';
import { FaPhoneVolume } from 'react-icons/fa';
import { Container } from '../atoms';
import SanityImage from './SanityImage';
import config from '../../utils/config';
import SocialIcons from './SocialIcons';
import CartBadge from '../cart/CartBadge';

const NavLink = styled(Link)(({ button }) => [
  tw`md:ml-2 px-4 py-3 font-medium transition duration-500 ease-in-out transform cursor-pointer`,
  button
    ? tw`px-6 bg-green md:self-center md:mt-0 text-white  hover:bg-white  mt-2 text-sm font-medium hover:text-black border hover:border-black`
    : tw`hover:text-gray-900 focus:text-gray-900 hover:bg-gray-200 focus:bg-gray-200 focus:outline-none text-base text-black`,
]);

const query = graphql`
  query HeaderNavigation {
    navigation: sanityNavigation {
      _id
      navLink {
        _key
        label
        secondaryLink {
          current
        }
        target {
          title
          slug {
            current
          }
        }
        showButton
      }
    }
    sanityGlobals {
      email
      phone
      logoImage {
        asset {
          gatsbyImageData(
            width: 191
            height: 88
            placeholder: BLURRED
            formats: [AUTO, WEBP, AVIF]
          )
          url
        }
      }
    }
  }
`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const cartItems = useStoreState((state) => state.cart.items);
  const { navigation, sanityGlobals } = useStaticQuery(query);
  const toggleOpen = useCallback(() => {
    setOpen((status) => !status);
  }, []);

  return (
    <Container tw="text-black bg-white pb-2 pt-2">
      <div tw="text-green flex mx-auto justify-center md:justify-between md:px-6 items-center">
        <div tw="flex items-center mb-1">
          {sanityGlobals.email && (
            <a
              href={`mailto:${sanityGlobals.email}`}
              tw="text-lg hidden md:flex items-center pl-2 pr-4 font-semibold"
            >
              <MdEmail tw="h-6 w-6 mr-2" /> <span>{sanityGlobals.email}</span>
            </a>
          )}
          {sanityGlobals.phone && (
            <a
              href={`tel:${sanityGlobals.phone}`}
              tw="text-lg hidden md:flex items-center font-semibold"
            >
              <FaPhoneVolume tw="h-6 w-6 mr-2" />{' '}
              <span>{sanityGlobals.phone}</span>
            </a>
          )}
        </div>
        <div tw="flex justify-center items-center">
          <CartBadge value={cartItems.length} />
          <SocialIcons variant="primary" flex />
        </div>
      </div>

      <div tw="flex flex-col px-4 mx-auto md:items-center md:justify-between md:flex-row md:px-6">
        <div tw="flex flex-row items-center justify-between p-2">
          <Link
            to="/"
            tw="flex cursor-pointer text-lg font-semibold focus:outline-none"
          >
            <div tw="inline-flex items-center w-32">
              {sanityGlobals.logoImage && (
                <SanityImage
                  fluid={sanityGlobals.logoImage.asset.gatsbyImageData}
                  alt={`${config.siteName} logo`}
                />
              )}
            </div>
          </Link>
          <button
            tw="rounded-lg md:hidden focus:outline-none"
            onClick={toggleOpen}
            // onBlur={toggleOpen}
            type="button"
          >
            <span tw="w-6 h-6">
              {!open ? (
                <BiMenuAltRight tw="h-[40px] w-[40px]" />
              ) : (
                <AiOutlineClose tw="h-[40px] w-[40px]" />
              )}
            </span>
          </button>
        </div>
        <nav
          css={[
            tw`md:pb-0 md:flex md:justify-end md:flex-row flex-col flex-grow hidden pb-4`,
            open ? tw`flex` : tw`hidden`,
          ]}
        >
          {navigation &&
            navigation.navLink.map((el) => {
              if (!el) {
                return null;
              }
              const primaryLink =
                el.target && el.target.slug && el.target?.slug?.current;
              const secondarySlug =
                el.secondaryLink && el.secondaryLink?.current;
              return (
                <NavLink
                  key={el._key}
                  to={primaryLink ? `/${primaryLink}` : `${secondarySlug}`}
                  button={el?.showButton || undefined}
                >
                  {el.label}
                </NavLink>
              );
            })}
        </nav>
      </div>
    </Container>
  );
}
